import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, BellRing, Check, Copy, Download, Link2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { formatDateTimeNumeric as formatDate } from '../../utils/format';
import {
  downloadDocument,
  downloadSubmission,
  getDocument,
  remindSigner,
  signatureKeys,
} from './signaturesApi';
import StatusBadge from './components/StatusBadge';
import { signerColor } from './lib/fieldTypes';

/**
 * Sender's per-document view: signer roster with progress (agreements) or the
 * public link + submissions list (waivers), plus reminders and downloads —
 * OtterWaiver's Signatory page.
 */
export default function SignatureDetailPage() {
  const { id } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: signatureKeys.detail(id),
    queryFn: () => getDocument(id),
  });

  const remind = useMutation({
    mutationFn: (signerId) => remindSigner({ id, signerId }),
    onSuccess: () => toast.success('Reminder sent'),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-red-600">{getErrorMessage(error)}</p>;
  }

  const { document: doc, signers } = data;
  const isWaiver = doc.type === 'waiver';
  const named = signers.filter((s) => s.kind === 'named');
  const submissions = signers.filter((s) => s.kind === 'public');
  const waiverUrl = doc.publicSlug ? `${window.location.origin}/w/${doc.publicSlug}` : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/signatures"
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" /> Signatures
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-slate-900">
          {doc.title}
        </h1>
        <StatusBadge status={doc.status} />
        {doc.status !== 'draft' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              downloadDocument(doc.id, doc.title).catch((err) =>
                toast.error(getErrorMessage(err)),
              )
            }
          >
            <Download className="h-4 w-4" /> Download PDF
          </Button>
        )}
      </div>

      <p className="text-sm text-slate-500">
        Created {formatDate(doc.createdAt)}
        {doc.publishedAt && ` · sent ${formatDate(doc.publishedAt)}`}
        {doc.completedAt && ` · completed ${formatDate(doc.completedAt)}`}
        {doc.expiresAt && ` · expires ${formatDate(doc.expiresAt)}`}
      </p>

      {/* Printed as "Reference ID" on the signed PDF's certificate page — shown
          here so a holder of that PDF can match it back to this document. */}
      <div className="flex items-center gap-2 text-sm">
        <span className="shrink-0 text-slate-500">Reference</span>
        <code className="min-w-0 flex-1 truncate rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
          {doc.id}
        </code>
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
            await navigator.clipboard.writeText(doc.id);
            toast.success('Reference copied');
          }}
        >
          <Copy className="h-4 w-4" /> Copy
        </Button>
      </div>

      {isWaiver ? (
        <>
          {waiverUrl && doc.status === 'published' && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3">
              <Link2 className="h-4 w-4 shrink-0 text-brand-600" />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{waiverUrl}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(waiverUrl);
                  toast.success('Link copied');
                }}
              >
                <Copy className="h-4 w-4" /> Copy
              </Button>
            </div>
          )}
          <section>
            <h2 className="mb-2 text-sm font-semibold text-slate-800">
              Submissions ({submissions.length})
            </h2>
            {submissions.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 bg-slate-100 px-4 py-8 text-center text-sm text-slate-400">
                No one has signed this document yet — share the link above.
              </p>
            ) : (
              <RosterTable
                signers={submissions}
                actionsFor={(signer) => (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      downloadSubmission(
                        doc.id,
                        signer.id,
                        `${doc.title}-${signer.firstName}`,
                      ).catch((err) => toast.error(getErrorMessage(err)))
                    }
                  >
                    <Download className="h-4 w-4" /> Copy
                  </Button>
                )}
              />
            )}
          </section>
        </>
      ) : (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-slate-800">
            Recipients ({named.filter((s) => s.isSigned).length}/{named.length} signed)
          </h2>
          <RosterTable
            signers={named}
            showColor
            actionsFor={(signer) =>
              !signer.isSigned && doc.status === 'published' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={remind.isPending && remind.variables === signer.id}
                  onClick={() => remind.mutate(signer.id)}
                >
                  <BellRing className="h-4 w-4" /> Remind
                </Button>
              ) : null
            }
          />
        </section>
      )}
    </div>
  );
}

function RosterTable({ signers, actionsFor, showColor = false }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Signer</th>
            <th className="px-4 py-3 font-semibold">Email</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Viewed</th>
            <th className="px-4 py-3 font-semibold">Signed</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {signers.map((signer) => (
            <tr key={signer.id}>
              <td className="px-4 py-3">
                <span className="flex items-center gap-2 font-medium text-slate-800">
                  {showColor && (
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: signerColor(signer.sequence) }}
                    />
                  )}
                  {signer.firstName} {signer.lastName}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-500">{signer.email}</td>
              <td className="px-4 py-3">
                {signer.isSigned ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                    <Check className="h-3 w-3" /> Signed
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                    Pending
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-slate-500">{formatDate(signer.timeStamps?.viewed)}</td>
              <td className="px-4 py-3 text-slate-500">{formatDate(signer.timeStamps?.signed)}</td>
              <td className="px-4 py-3 text-right">{actionsFor?.(signer)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
